import { Box, Grid, makeStyles, Typography, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { useQuery } from "@tanstack/react-query";
import componentStylesGeneric from "assets/theme/views/admin/generic.js";
import GenericButton from "components/Buttons/GenericButton";
import FormSwitch from "components/FormFields/FormSwitch";
import PageTitleHeader from "components/Headers/PageTitleHeader";
import { listWorkCenterEmployers } from "crud/workCentersEmployer.crud";
import { ConditionGuard } from "guards/ConditionGuard";
import useFilterSync from "hooks/useFilterSync";
import { useModal } from "hooks/useModal";
import moment from "moment";
import React, { useCallback, useRef, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { connect, useDispatch } from "react-redux";
import getDataSetByNameAction from "redux/actions/datasetsAction";
import { getReportAction } from "redux/actions/reportsAction";
import {
    createWorkCenterEmployerAction,
    getWorkCenterEmployerByIdAction,
    listWorkCenterEmployersAction,
    updateWorkCenterEmployerAction,
} from "redux/actions/workCentersEmployerAction";
import { REPORT_CONSTS, REPORT_FILE_NAME, REPORT_TYPES } from "utils/const";
import BulkActionConfirmModal from "./BulkActionConfirmModal";
import FilterSection from "./FilterSection";
import InactiveEmployeeModal from "./InactiveEmployeeModal";
import ResultSection from "./ResultSection";
import WorkCentersEmployeesModal from "./WorkCentersEmployeesModal";

const useStylesGeneric = makeStyles(componentStylesGeneric);

const DEFAULT_FORM_VALUES = {
  name: "",
  ssn: "",
  nif: "",
  documentNumber: "",
  startDate: null,
  endDate: null,
  workCenterId: "",
  professionalCategoryId: "",
  employmentStatusId: "",
  genderId: "",
  nationalityId: "",
  showInactive: false,
  expiringIn30Days: false,
  SalaryAlertOn: false,
  pageIndex: 1,
  pageSize: 15,
};

const QUERY_PARAMS_PREFIX = "wce_";

const WorkCentersEmployeesInner = ({
  sortColumn,
  setSortColumn,
  sortDirection,
  setSortDirection,
  handleOpenDialog,
  bulkSelectionMode,
  selectedEmployees,
  setSelectedEmployees,
  onBulkAction,
  bulkActionType,
  onBulkActionConfirmed,
  getBulkActionCallbacks,
  onToggleBulkSelection,
  onRefetchReady,
  FromRENT = false,
}) => {
  const classes = useStylesGeneric();
  const { getValues, setValue, control } = useFormContext();
  const dispatch = useDispatch();

  const fetchWorkCenterEmployees = async () => {
    const values = getValues();

    const queryParams = {
      ...values,
      startDate: values?.startDate ? moment(values.startDate).format() : null,
      endDate: values?.endDate ? moment(values.endDate).format() : null,
      sortColumn: sortColumn,
      sortDirection: sortDirection,
    };

    return listWorkCenterEmployers(queryParams);
  };

  const { refetch, isLoading } = useQuery({
    queryKey: ["listWorkCenterEmployers"],
    queryFn: fetchWorkCenterEmployees,
    staleTime: 0,
    refetchOnMount: true,
    enabled: false,
  });

  React.useEffect(() => {
    onRefetchReady?.(refetch);
  }, [refetch]); // eslint-disable-line react-hooks/exhaustive-deps

  const { handleFilterSubmit, handleFilterReset, updatePagination, syncFiltersFromUrl } = useFilterSync({
    defaultValues: DEFAULT_FORM_VALUES,
    syncOnMount: false,
    syncOnUrlChange: true,
    syncPagination: true,
    queryParamsPrefix: QUERY_PARAMS_PREFIX,
    pagination: {
      pageIndexKey: "pageIndex",
      pageSizeKey: "pageSize",
      defaultPageSize: DEFAULT_FORM_VALUES.pageSize,
    },
    onFilterChange: () => {
      setTimeout(() => {
        refetch();
      }, 0);
    },
  });

  React.useEffect(() => {
    syncFiltersFromUrl();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = async () => {
    setValue("pageIndex", 1);
    handleFilterSubmit(getValues());
  };

  const handleClearFilters = async () => {
    handleFilterReset();
  };

  const handleExport = (extension) => {
    const values = getValues();

    const queryParams = {
      ...values,
      pageIndex: 1,
      pageSize: REPORT_CONSTS.MAX_RESULTS,
      startDate: values?.startDate ? moment(values.startDate).format() : null,
      endDate: values?.endDate ? moment(values.endDate).format() : null,
      sortColumn: sortColumn,
      sortDirection: sortDirection,
    };

    const reportPayload = {
      reportEnumKey: REPORT_TYPES.WORKCENTER_EMPLOYEES_SEARCH_RESULTS,
      parameters: JSON.stringify(queryParams),
    };

    const fileName = `${REPORT_FILE_NAME.WORKCENTER_EMPLOYEES_SEARCH_RESULTS}${moment().format(
      "YYYYMMDDHHmm"
    )}${extension}`;

    dispatch(getReportAction(reportPayload, fileName));
  };

  const handleAutoSearch = async () => {
    setValue("pageIndex", 1);
    handleFilterSubmit(getValues());
  };

  const handlePaginationChange = async (pageIndex) => {
    setValue("pageIndex", pageIndex);
    updatePagination(pageIndex, getValues("pageSize"));
    await refetch();
  };

  const handleResetToFirstPage = () => {
    updatePagination(1, getValues("pageSize"));
  };

  return (
    <>
      <Grid item xs={12} md={12}>
        <FilterSection
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          isLoading={isLoading}
          exportDisabled={isLoading}
          onExport={handleExport}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          onAutoSearch={handleAutoSearch}
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <Typography variant="h3" style={{ marginBottom: "1rem" }}>
          Na tabela em baixo estão listados todos os trabalhadores que integram a lista que registou na sua área de
          utilizador e serão integrados no relatório RENT. <br />
          Poderá editar as informações directamente entrando no detalhe de cada centro de trabalho. Após gravar ou
          adicionar o centro de trabalho, a lista no menu de utilizadores será actualizada.
        </Typography>
      </Grid>

      <Grid item xs={12} md={12}>
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
            marginBottom: "1rem",
          }}
        >
          <GenericButton
            typeSubmit={false}
            size="medium"
            color="secondary"
            onClick={onToggleBulkSelection}
          >
            {bulkSelectionMode ? "Cancelar Seleção" : "Activar/Inactivar Trabalhadores"}
          </GenericButton>

          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "16px",
              flexWrap: "wrap",
              marginLeft: "auto",
            }}
          >
            <ConditionGuard condition={!FromRENT}>
              <FormSwitch
                name="showInactive"
                label="Trabalhadores Inactivos"
                control={control}
                defaultValue={false}
              />
            </ConditionGuard>
            <FormSwitch
              name="expiringIn30Days"
              label="Documentos a expirar"
              control={control}
              defaultValue={false}
            />
            <FormSwitch
              name="SalaryAlertOn"
              label="Alerta Salarial"
              control={control}
              defaultValue={false}
            />
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} md={12}>
        <Box classes={{ root: classes.tableResultBoxRoot }}>
          <ResultSection
            handleOpenDialog={handleOpenDialog}
            sortColumn={sortColumn}
            setSortColumn={setSortColumn}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            bulkSelectionMode={bulkSelectionMode}
            selectedEmployees={selectedEmployees}
            setSelectedEmployees={setSelectedEmployees}
            onBulkAction={onBulkAction}
            bulkActionType={bulkActionType}
            onBulkActionConfirmed={onBulkActionConfirmed}
            getBulkActionCallbacks={getBulkActionCallbacks}
            onPaginationChange={handlePaginationChange}
            onResetToFirstPage={handleResetToFirstPage}
          />
        </Box>
      </Grid>
    </>
  );
};

const WorkCentersEmployees = (props) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const refetchListRef = useRef(null);

  const handleSaveSuccess = useCallback(() => {
    refetchListRef.current?.();
  }, []);

  const [openDialog, setOpenDialog] = React.useState(false);
  const [selectedWorkCenterEmployer, setSelectedWorkCenterEmployer] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [bulkSelectionMode, setBulkSelectionMode] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [bulkActionModalOpen, setBulkActionModalOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState(null);
  const [bulkActionCallbacks, setBulkActionCallbacks] = useState({ activate: null, inactivate: null });
  const [bulkActionExecuting, setBulkActionExecuting] = useState(false);

  const {
    isOpen: exitEmployeeModalOpen,
    closeModal: handleCloseRejectionModal,
    openModal: handleOpenRejectionModal,
  } = useModal();

  const handleOpenDialog = (workCenterEmployer) => {
    setSelectedWorkCenterEmployer(workCenterEmployer);
    setIsCreating(isCreating);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedWorkCenterEmployer(null);
  };

  const handleToggleBulkSelection = () => {
    setBulkSelectionMode(!bulkSelectionMode);
    setSelectedEmployees([]);
  };

  const handleBulkAction = (actionType) => {
    setBulkActionType(actionType);
    setBulkActionModalOpen(true);
  };

  const handleCloseBulkActionModal = () => {
    setBulkActionModalOpen(false);
    setBulkActionType(null);
  };

  const handleBulkActionConfirmed = async (data) => {
    setBulkActionExecuting(true);

    try {
      if (bulkActionType === "activate" && bulkActionCallbacks.activate) {
        await bulkActionCallbacks.activate();
      } else if (bulkActionType === "inactivate" && bulkActionCallbacks.inactivate) {
        await bulkActionCallbacks.inactivate(data);
      }

      setBulkSelectionMode(false);
      setSelectedEmployees([]);
      handleCloseBulkActionModal();
    } finally {
      setBulkActionExecuting(false);
    }
  };

  const handleGetBulkActionCallbacks = useCallback((callbacks) => {
    setBulkActionCallbacks(callbacks);
  }, []);

  const form = useForm({
    mode: "onChange",
    defaultValues: DEFAULT_FORM_VALUES,
  });

  return (
    <FormProvider {...form}>
      <Grid
        item
        xs={12}
        md={12}
        style={{
          marginBottom: "1rem",
        }}
      >
        <Box
          display="flex"
          flexDirection={isSmDown ? "column" : "row"}
          justifyContent="start"
          alignItems={isSmDown ? "stretch" : "center"}
          gridGap={12}
          width="100%"
        >
          <PageTitleHeader title="Trabalhadores" />
          <Box width={isSmDown ? "100%" : "auto"}>
            <GenericButton
              typeSubmit={false}
              size="medium"
              color="tertiary"
              onClick={() => handleOpenDialog()}
              style={
                isSmDown
                  ? { width: "100%" }
                  : {
                      marginBottom: "37px",
                    }
              }
            >
              Criar Trabalhador
            </GenericButton>
          </Box>
        </Box>
      </Grid>
      <WorkCentersEmployeesInner
        sortColumn={sortColumn}
        setSortColumn={setSortColumn}
        sortDirection={sortDirection}
        setSortDirection={setSortDirection}
        handleOpenDialog={handleOpenDialog}
        bulkSelectionMode={bulkSelectionMode}
        selectedEmployees={selectedEmployees}
        setSelectedEmployees={setSelectedEmployees}
        onBulkAction={handleBulkAction}
        bulkActionType={bulkActionType}
        onBulkActionConfirmed={handleBulkActionConfirmed}
        getBulkActionCallbacks={handleGetBulkActionCallbacks}
        onToggleBulkSelection={handleToggleBulkSelection}
        onRefetchReady={(fn) => { refetchListRef.current = fn; }}
        FromRENT={props?.FromRENT}
      />
      <ConditionGuard condition={exitEmployeeModalOpen}>
        <InactiveEmployeeModal
          selectedWorkCenterEmployer={selectedWorkCenterEmployer}
          open={exitEmployeeModalOpen}
          handleClose={handleCloseRejectionModal}
          handleCloseDialog={handleCloseDialog}
          onSaveSuccess={handleSaveSuccess}
        />
      </ConditionGuard>
      <ConditionGuard condition={openDialog}>
        <WorkCentersEmployeesModal
          openDialog={openDialog}
          handleCloseDialog={handleCloseDialog}
          getDataSetByName={props.getDataSetByName}
          props={props}
          isCreating={isCreating}
          selectedWorkCenterEmployer={selectedWorkCenterEmployer}
          handleOpenExitEmployeeModal={handleOpenRejectionModal}
          onSaveSuccess={handleSaveSuccess}
        />
      </ConditionGuard>
      <ConditionGuard condition={bulkActionModalOpen}>
        <BulkActionConfirmModal
          open={bulkActionModalOpen}
          handleClose={handleCloseBulkActionModal}
          actionType={bulkActionType}
          onConfirm={handleBulkActionConfirmed}
          isLoading={bulkActionExecuting}
          selectedEmployees={selectedEmployees}
        />
      </ConditionGuard>
    </FormProvider>
  );
};

const mapStateToProps = (state) => ({ ...state });

const mapDispatchToProps = (dispatch) => ({
  getDataSetByName: (name, addEmptyField) => dispatch(getDataSetByNameAction(name, addEmptyField)),
  createWorkCenterEmployer: (data) => dispatch(createWorkCenterEmployerAction(data)),
  getFilteredWorkCenterEmployer: (data) => dispatch(listWorkCenterEmployersAction(data)),
  getWorkCenterEmployerById: (id) => dispatch(getWorkCenterEmployerByIdAction(id)),
  updateWorkCenterEmployer: (data) => dispatch(updateWorkCenterEmployerAction(data)),
});
export default connect(mapStateToProps, mapDispatchToProps)(WorkCentersEmployees);
